/**
 * Device Fingerprinting Utility
 * Generates a unique fingerprint for the browser/device combination
 */

/**
 * Generate a unique device fingerprint based on browser characteristics
 * This creates a persistent identifier across sessions on the same browser
 */
export async function generateDeviceFingerprint(): Promise<string> {
  const components: string[] = [];

  // Browser and platform information
  components.push(navigator.userAgent);
  components.push(navigator.language);
  components.push(navigator.platform);
  components.push(String(navigator.hardwareConcurrency || ''));
  components.push(String(navigator.maxTouchPoints || 0));

  // Screen information
  components.push(`${screen.width}x${screen.height}`);
  components.push(`${screen.colorDepth}`);
  components.push(String(screen.pixelDepth));
  components.push(`${window.devicePixelRatio}`);

  // Timezone
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
  components.push(String(new Date().getTimezoneOffset()));

  // Canvas fingerprinting (lightweight version)
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      canvas.width = 200;
      canvas.height = 50;
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('ADLabs Fingerprint', 2, 15);
      components.push(canvas.toDataURL());
    }
  } catch (e) {
    // Canvas fingerprinting might be blocked
    components.push('canvas-blocked');
  }

  // WebGL fingerprinting (if available)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        components.push(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL));
        components.push(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
      }
    }
  } catch (e) {
    // WebGL might not be available
    components.push('webgl-unavailable');
  }

  // Fonts detection (basic check)
  const fonts = ['Arial', 'Verdana', 'Courier New', 'Georgia', 'Times New Roman'];
  const availableFonts = fonts.filter(font => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;

    ctx.font = `12px monospace`;
    const baselineWidth = ctx.measureText('mmmmmmmmmmlli').width;

    ctx.font = `12px '${font}', monospace`;
    const testWidth = ctx.measureText('mmmmmmmmmmlli').width;

    return baselineWidth !== testWidth;
  });
  components.push(availableFonts.join(','));

  // Combine all components and hash
  const fingerprint = components.join('|');

  // Use SubtleCrypto API to hash the fingerprint
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(fingerprint);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (e) {
    // Fallback to simple hash if SubtleCrypto is not available
    return simpleHash(fingerprint);
  }
}

/**
 * Simple hash function as fallback
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16).padStart(16, '0');
}

/**
 * Get or create device fingerprint
 * This function checks localStorage first for a cached fingerprint,
 * generates a new one if not found, and caches it for future use
 */
export async function getDeviceFingerprint(): Promise<string> {
  const STORAGE_KEY = 'device_fingerprint';

  // Check if we already have a fingerprint in localStorage
  const cached = localStorage.getItem(STORAGE_KEY);
  if (cached) {
    return cached;
  }

  // Generate new fingerprint
  const fingerprint = await generateDeviceFingerprint();

  // Cache it
  localStorage.setItem(STORAGE_KEY, fingerprint);

  return fingerprint;
}

/**
 * Clear cached device fingerprint
 * Useful for testing or when explicitly resetting the device
 */
export function clearDeviceFingerprint(): void {
  localStorage.removeItem('device_fingerprint');
}
